// src/utils/role.tsx

import { useSelector } from 'react-redux';
import { store } from '../redux/store';
import type { RootState } from '../redux/store';

const ADMIN_ROLES = ["ADMIN", "PLATFORM_ADMIN"];
const ISG_ROLES = ["ISG_MANAGER", "ISG_REGULAR"];
const FIRM_ROLES = ["FIRM_MANAGER", "FIRM_REGULAR"];

const isIsgRole = (roles: string[]): boolean => {
  return roles.some((r) => ISG_ROLES.includes(r));
};

const isFirmRole = (roles: string[]): boolean => {
  return roles.some((r) => FIRM_ROLES.includes(r));
};

const getAuthUser = () => {
  return store.getState().auth.user;
};

/**
 * Custom hook to get current user's roles from Redux store.
 */
export const useRoles = (): string[] => {
  return useSelector((state: RootState) => state.auth.user?.roles ?? []);
};

/**
 * Non-hook helper – safe to use outside React (axios, utils, etc.)
 */
export const getCurrentRoles = (): string[] => {
  try {
    return getAuthUser()?.roles ?? [];
  } catch {
    return [];
  }
};

/**
 * Returns firmId based on current user's role.
 *
 * - ISG users: selected firmId is read from localStorage
 * - Firm users: firmId is read from auth.user
 */
export const getCurrentFirmId = (): string | null => {
  try {
    const user = getAuthUser();
    const roles = user?.roles ?? [];

    if (isIsgRole(roles)) {
      return localStorage.getItem('firmId');
    }

    return user?.firmId ?? null;
  } catch {
    return null;
  }
};

export const isIsgManagerRole = (): boolean => {
  return getCurrentRoles().includes('ISG_MANAGER');
};

/**
 * Custom hook: returns true if user has any admin-level role.
 */
export const useIsAdmin = (): boolean => {
  const roles = useRoles();
  return roles.some((r) => ADMIN_ROLES.includes(r));
};

/**
 * Custom hook: returns true if user is an ISG user (manager or regular).
 */
export const useIsIsgUser = (): boolean => {
  const roles = useRoles();
  return isIsgRole(roles);
};

/**
 * Custom hook: returns true if user has ISG_MANAGER role.
 */
export const useIsIsgManager = (): boolean => {
  const roles = useRoles();
  return roles.includes('ISG_MANAGER');
};

/**
 * Custom hook: returns true if user is a firm user
 * (has at least one role, but is not admin and not ISG user).
 */
export const useIsFirmUser = (): boolean => {
  const roles = useRoles();

  if (roles.length === 0) {
    return false;
  }

  const isAdmin = roles.some((r) => ADMIN_ROLES.includes(r));
  const isIsgUser = isIsgRole(roles);
  const isFirm = isFirmRole(roles);

  return isFirm && !isAdmin && !isIsgUser;
};